import React, { Fragment, useEffect, useState, useRef } from "react";
import { toast, Zoom } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Box, IconButton, Tooltip } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Plus, Eye, Copy, Check, Edit } from "react-feather";
import {
  Card,
  CardHeader,
  CardBody,
  Col,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input
} from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import Breadcrumbs from "../../layout/breadcrumb";
import TableSkeleton from "../../loader/TableSkeleton";
import DataNotFound from "../../DataNoteFound/DataNotFound";
import { MuiTabelStyle } from "../../utils/helper";
import { fetchEventsList, createEvent, updateEventApi, fetchEventDetail, fetchEventAccessUsers } from "../../api/Events/EventsApi";
import { Temple_Trust_List } from "../../api/TempleTrustApi/TempleTrustApi";
import { ERROR_CODE } from "../../constant";
import LogOut from "../../utils/logout";

const Events = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [sortBy, setSortBy] = useState("createdAt");

  // Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [eventName, setEventName] = useState("");
  const [description, setDescription] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [location, setLocation] = useState("");
  const [whatsappMessage, setWhatsappMessage] = useState("");
  const [isEdit, setIsEdit] = useState(false);
  const [editEventId, setEditEventId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Payment configuration states
  const [trustList, setTrustList] = useState([]);
  const [isPaymentEnabled, setIsPaymentEnabled] = useState(false);
  const [amount, setAmount] = useState("");
  const [trustId, setTrustId] = useState("");
  const [tokenStartValue, setTokenStartValue] = useState("");
  const [isRegistrationActive, setIsRegistrationActive] = useState(true);

  // Shared user access list & editable by user states
  const [accessUsersList, setAccessUsersList] = useState([]);
  const [accessUserIds, setAccessUserIds] = useState([]);
  const [isEditableByUser, setIsEditableByUser] = useState(false);
  const [eventCreatedBy, setEventCreatedBy] = useState(null);

  const getTrusts = async () => {
    try {
      const { data } = await Temple_Trust_List({ showAll: true });
      if (data?.status === 200 && data?.payload?.data) {
        setTrustList(data.payload.data);
      }
    } catch (error) {
      console.error("Failed to load trust list:", error);
    }
  };

  const getAccessUsers = async () => {
    try {
      const { data } = await fetchEventAccessUsers();
      if (data?.status === 200 && data?.payload?.data) {
        setAccessUsersList(data.payload.data);
      }
    } catch (error) {
      console.error("Failed to load access users list:", error);
    }
  };

  useEffect(() => {
    getTrusts();
    getAccessUsers();
  }, []);

  // Copied State
  const [copiedId, setCopiedId] = useState(null);

  // Dynamic Form Fields Builder State
  const [formFields, setFormFields] = useState([]);
  const [nameConfig, setNameConfig] = useState('single');

  const getEvents = async () => {
    setLoading(true);
    try {
      let paramsObj = {
        pageNumber: page,
        recordsPerPage: pageSize,
        sortOrder,
        sortBy
      };
      if (search) {
        paramsObj.search = JSON.stringify({ eventName: search });
      }

      const { data } = await fetchEventsList(paramsObj);
      if (data.status === 200) {
        setEvents(data.payload.data);
        setTotalRecords(data.pager.totalRecords);
      } else {
        setEvents([]);
      }
    } catch (error) {
      if (error?.response?.data?.status === ERROR_CODE.UNAUTHORIZED) {
        LogOut(navigate);
      }
      toast.error(error?.response?.data?.message || "Failed to load events", {
        autoClose: 1500,
        transition: Zoom
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = async (eventId) => {
    setIsEdit(true);
    setEditEventId(eventId);
    const loadingToast = toast.info("Fetching event details...", { autoClose: false });
    try {
      const { data } = await fetchEventDetail({ eventId });
      if (data.status === 200 && data.payload?.data) {
        const detail = data.payload.data;
        setEventName(detail.eventName || "");
        setDescription(detail.description || "");
        setEventDate(detail.eventDate || "");
        setLocation(detail.location || "");
        setWhatsappMessage(detail.whatsappMessage || "");
        setIsPaymentEnabled(!!detail.isPaymentEnabled);
        setAmount(detail.amount || "");
        setTrustId(detail.trustId || "");
        setIsEditableByUser(!!detail.isEditableByUser);
        setAccessUserIds(detail.accessUserIds || []);
        setEventCreatedBy(detail.createdBy || null);
        setTokenStartValue(detail.tokenStartValue !== undefined && detail.tokenStartValue !== null ? String(detail.tokenStartValue) : "");
        setIsRegistrationActive(detail.isRegistrationActive !== false);

        let parsedFormFields = [];
        if (detail.formFields) {
          if (typeof detail.formFields === 'string') {
            try {
              parsedFormFields = JSON.parse(detail.formFields);
            } catch (e) {
              parsedFormFields = [];
            }
          } else if (Array.isArray(detail.formFields)) {
            parsedFormFields = detail.formFields;
          }
        }

        // Extract and filter nameConfig if present
        const configField = parsedFormFields.find(f => f.fieldKey === 'name_config');
        setNameConfig(configField ? configField.label : 'single');
        const filteredFields = parsedFormFields.filter(f => f.fieldKey !== 'name_config');

        const fieldsWithRawOptions = filteredFields.map(field => {
          if (['dropdown', 'radio', 'checkbox'].includes(field.type) && Array.isArray(field.options)) {
            return {
              ...field,
              options: field.options.join(', ')
            };
          }
          return field;
        });
        setFormFields(fieldsWithRawOptions);
        setModalOpen(true);
      } else {
        toast.error("Failed to load event details");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error loading event details");
    } finally {
      toast.dismiss(loadingToast);
    }
  };

  useEffect(() => {
    getEvents();
  }, [page, pageSize, sortOrder, sortBy, search]);

  const handleCopyLink = (eventUuid, eventId) => {
    const link = `${window.location.origin}${process.env.PUBLIC_URL || ""}/EventRegister?eventUuid=${eventUuid}`;
    navigator.clipboard.writeText(link);
    setCopiedId(eventId);
    toast.success("Registration link copied successfully!", { autoClose: 1000 });
    setTimeout(() => {
      setCopiedId(null);
    }, 2000);
  };

  const handleAddField = () => {
    setFormFields([...formFields, {
      fieldKey: `field_${Date.now()}`,
      label: '',
      type: 'text',
      required: false,
      options: ''
    }]);
  };

  const handleRemoveField = (index) => {
    const updated = [...formFields];
    updated.splice(index, 1);
    setFormFields(updated);
  };

  const handleUpdateField = (index, key, value) => {
    const updated = [...formFields];
    updated[index][key] = value;
    setFormFields(updated);
  };

  // Drag-and-drop reordering
  const dragFieldIndex = useRef(null);

  const handleDragStart = (idx) => {
    dragFieldIndex.current = idx;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, targetIdx) => {
    e.preventDefault();
    const fromIdx = dragFieldIndex.current;
    if (fromIdx === null || fromIdx === targetIdx) return;
    const updated = [...formFields];
    const [moved] = updated.splice(fromIdx, 1);
    updated.splice(targetIdx, 0, moved);
    setFormFields(updated);
    dragFieldIndex.current = null;
  };

  const handleSubmitEvent = async (e) => {
    e.preventDefault();
    if (!eventName.trim()) {
      toast.error("Event Name is required");
      return;
    }

    if (isPaymentEnabled) {
      if (!amount || Number(amount) <= 0) {
        toast.error("Registration fee must be greater than 0");
        return;
      }
      if (!trustId) {
        toast.error("Please select a trust for receiving payments");
        return;
      }
    }

    setSubmitting(true);
    try {
      const processedFormFields = formFields.length > 0 ? formFields.map(field => {
        if (['dropdown', 'radio', 'checkbox'].includes(field.type)) {
          return {
            ...field,
            options: typeof field.options === 'string'
              ? field.options.split(',').map(s => s.trim()).filter(Boolean)
              : (Array.isArray(field.options) ? field.options : [])
          };
        }
        return field;
      }) : null;

      const finalFields = [...(processedFormFields || [])];
      finalFields.push({
        fieldKey: 'name_config',
        label: nameConfig,
        type: 'label',
        required: false
      });

      const payload = {
        eventName,
        description,
        eventDate,
        location,
        whatsappMessage,
        formFields: finalFields,
        isPaymentEnabled,
        amount: isPaymentEnabled ? Number(amount) : 0,
        trustId: isPaymentEnabled ? Number(trustId) : null,
        isEditableByUser,
        accessUserIds,
        tokenStartValue: tokenStartValue ? Number(tokenStartValue) : null,
        isRegistrationActive
      };

      if (isEdit) {
        const { data } = await updateEventApi(editEventId, payload);
        if (data.status === 200) {
          toast.success("Event updated successfully!", { autoClose: 1000 });
          setModalOpen(false);
          // Reset fields
          setEventName("");
          setDescription("");
          setEventDate("");
          setLocation("");
          setWhatsappMessage("");
          setFormFields([]);
          setNameConfig("single");
          setIsPaymentEnabled(false);
          setAmount("");
          setTrustId("");
          setIsEditableByUser(false);
          setAccessUserIds([]);
          setEventCreatedBy(null);
          setTokenStartValue("");
          setIsRegistrationActive(true);
          setIsEdit(false);
          setEditEventId(null);
          getEvents();
        }
      } else {
        const { data } = await createEvent(payload);
        if (data.status === 200) {
          toast.success("Event created successfully!", { autoClose: 1000 });
          setModalOpen(false);
          // Reset fields
          setEventName("");
          setDescription("");
          setEventDate("");
          setLocation("");
          setWhatsappMessage("");
          setFormFields([]);
          setNameConfig("single");
          setIsPaymentEnabled(false);
          setAmount("");
          setTrustId("");
          setIsEditableByUser(false);
          setAccessUserIds([]);
          setEventCreatedBy(null);
          setTokenStartValue("");
          setIsRegistrationActive(true);
          getEvents();
        }
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} event`);
    } finally {
      setSubmitting(false);
    }
  };

  const sorting = (col) => {
    if (col && col.length > 0) {
      const activeSort = col[0];
      setSortBy(activeSort.field);
      setSortOrder(activeSort.sort?.toUpperCase() || "DESC");
    } else {
      setSortBy("createdAt");
      setSortOrder("DESC");
    }
  };

  const defaultColumns = [
    {
      flex: 0.15,
      minWidth: 180,
      field: "eventName",
      headerName: "Event Name"
    },
    {
      flex: 0.15,
      minWidth: 150,
      field: "eventDate",
      headerName: "Event Date"
    },
    {
      flex: 0.15,
      minWidth: 180,
      field: "location",
      headerName: "Location"
    },
    {
      flex: 0.1,
      minWidth: 120,
      field: "newRegistrations",
      headerName: "New (Count)",
      renderCell: (params) => (
        <span className="badge badge-success font-medium px-2 py-1" style={{ fontSize: "12px" }}>
          👤 {params.value || 0} New
        </span>
      )
    },
    {
      flex: 0.1,
      minWidth: 120,
      field: "existingRegistrations",
      headerName: "Existing (Count)",
      renderCell: (params) => (
        <span className="badge badge-info font-medium px-2 py-1" style={{ fontSize: "12px" }}>
          👥 {params.value || 0} Exist
        </span>
      )
    },
    {
      flex: 0.1,
      minWidth: 120,
      field: "totalRegistrations",
      headerName: "Total Registrations",
      renderCell: (params) => (
        <strong style={{ fontSize: "14px", color: "#4a1b8a" }}>
          🚀 {params.value || 0}
        </strong>
      )
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      minWidth: 180,
      renderCell: (params) => {
        const row = params.row;
        const layout = localStorage.getItem("layout") || "compact-wrapper";
        return (
          <div className="d-flex align-items-center gap-2">
            <Tooltip title="View Registered Users">
              <Link to={`${process.env.PUBLIC_URL}/Events/event/${row.eventId}/${layout}`}>
                <IconButton color="primary" size="small">
                  <Eye size={18} />
                </IconButton>
              </Link>
            </Tooltip>

            <Tooltip title={copiedId === row.eventId ? "Copied!" : "Copy Registration Link"}>
              <IconButton
                onClick={() => handleCopyLink(row.eventUuid, row.eventId)}
                color={copiedId === row.eventId ? "success" : "secondary"}
                size="small"
              >
                {copiedId === row.eventId ? <Check size={18} /> : <Copy size={18} />}
              </IconButton>
            </Tooltip>

            <Tooltip title="Edit Event">
              <IconButton
                onClick={() => handleEditClick(row.eventId)}
                color="info"
                size="small"
              >
                <Edit size={18} />
              </IconButton>
            </Tooltip>
          </div>
        );
      }
    }
  ];

  return (
    <Fragment>
      <Breadcrumbs parent="Events" title="Event Management" />
      <Container fluid={true}>
        <Row>
          <Col sm="12">
            <Card className="border-0 shadow-sm" style={{ borderRadius: "16px", overflow: "hidden" }}>
              <CardHeader
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                  borderBottom: "1px solid rgba(0,0,0,0.05)",
                  padding: "20px 24px"
                }}
              >
                <div>
                  <h3 className="m-0 font-weight-bold" style={{ color: "#4a1b8a", letterSpacing: "0.5px" }}>
                    Events List
                  </h3>
                  <small className="text-muted">Manage events and track registration counts</small>
                </div>
                <Button
                  color="primary"
                  className="d-flex align-items-center gap-2 shadow-sm font-weight-bold"
                  onClick={() => {
                    setIsEdit(false);
                    setEditEventId(null);
                    setEventName("");
                    setDescription("");
                    setEventDate("");
                    setLocation("");
                    setWhatsappMessage("");
                    setFormFields([]);
                    setNameConfig("single");
                    setIsPaymentEnabled(false);
                    setAmount("");
                    setTrustId("");
                    setIsEditableByUser(false);
                    setAccessUserIds([]);
                    setTokenStartValue("");
                    setIsRegistrationActive(true);
                    setEventCreatedBy(null);
                    setModalOpen(true);
                  }}
                  style={{
                    borderRadius: "30px",
                    padding: "10px 24px",
                    backgroundColor: "#7c3abf",
                    border: "none",
                    transition: "all 0.2s"
                  }}
                >
                  <Plus size={18} /> Create Event
                </Button>
              </CardHeader>
              <div className="p-4">
                <Row className="mb-4">
                  <Col md="4">
                    <Input
                      type="text"
                      placeholder="🔍 Search Event Name..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      style={{
                        borderRadius: "10px",
                        border: "1.5px solid #e0d6f5",
                        padding: "10px 16px"
                      }}
                    />
                  </Col>
                </Row>
                <Box
                  sx={{
                    "& .even": {
                      backgroundColor: "#fcfaff"
                    },
                    "& .odd": {
                      backgroundColor: "white"
                    }
                  }}
                >
                  {loading ? (
                    <TableSkeleton />
                  ) : events.length > 0 ? (
                    <DataGrid
                      sx={MuiTabelStyle}
                      autoHeight
                      pagination
                      rows={events}
                      rowCount={totalRecords}
                      pageSize={pageSize}
                      getRowId={(row) => row.eventId}
                      columns={defaultColumns}
                      page={page - 1}
                      rowsPerPageOptions={[5, 10, 25, 50]}
                      onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                      onPageChange={(newPageNo) => setPage(newPageNo + 1)}
                      paginationMode="server"
                      disableSelectionOnClick
                      checkboxSelection={false}
                      sortingMode="server"
                      onSortModelChange={sorting}
                      getRowClassName={(params) =>
                        params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
                      }
                    />
                  ) : (
                    <DataNotFound />
                  )}
                </Box>
              </div>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Event Modal */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)} size="lg" centered>
        <ModalHeader toggle={() => setModalOpen(!modalOpen)} className="font-weight-bold" style={{ color: "#4a1b8a" }}>
          {isEdit ? "Edit Event" : "Create New Event"}
        </ModalHeader>
        <Form onSubmit={handleSubmitEvent}>
          <ModalBody className="p-4">
            <FormGroup className="mb-3">
              <Label className="font-weight-bold">Event Name <span className="text-danger">*</span></Label>
              <Input
                type="text"
                value={eventName}
                onChange={(e) => setEventName(e.target.value)}
                placeholder="e.g. Jivan Utkars Mohotsav"
                required
                style={{ borderRadius: "8px" }}
              />
            </FormGroup>
            <FormGroup className="mb-3">
              <Label className="font-weight-bold">Description</Label>
              <Input
                type="textarea"
                rows="3"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter event details/description..."
                style={{ borderRadius: "8px" }}
              />
            </FormGroup>
            <FormGroup className="mb-3">
              <Label className="font-weight-bold">Event Date</Label>
              <Input
                type="text"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                placeholder="e.g. ૨૬ થી ૩૧ મે ૨૦૨૬ — રાત્રે ૯:૦૦ વાગ્યે"
                style={{ borderRadius: "8px" }}
              />
            </FormGroup>
            <FormGroup className="mb-3">
              <Label className="font-weight-bold">Location</Label>
              <Input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="e.g. દેવી ફાર્મ, કિરણચોક, સુરત"
                style={{ borderRadius: "8px" }}
              />
            </FormGroup>
            <FormGroup className="mb-3">
              <Label className="font-weight-bold">WhatsApp Message Template</Label>
              <Input
                type="textarea"
                rows="4"
                value={whatsappMessage}
                onChange={(e) => setWhatsappMessage(e.target.value)}
                placeholder="Enter WhatsApp template. E.g. Jai Swaminarayan! Dear {{name}}, thank you for registering for the event on {{eventDate}}."
                style={{ borderRadius: "8px" }}
              />
              <small className="text-muted">
                You can use template placeholders like {"{{name}}"}, {"{{mobile}}"}, {"{{address}}"}, {"{{gender}}"}, {"{{city}}"} or any custom field keys.
              </small>
            </FormGroup>

            <FormGroup className="mb-3">
              <Label className="font-weight-bold d-block">Name Field Layout Configuration</Label>
              <div className="d-flex align-items-center mt-2" style={{ gap: '2rem' }}>
                <div 
                  className="d-flex align-items-center" 
                  style={{ cursor: "pointer" }} 
                  onClick={() => setNameConfig('single')}
                >
                  <input
                    type="radio"
                    name="nameConfig"
                    checked={nameConfig === 'single'}
                    onChange={() => setNameConfig('single')}
                    style={{ cursor: "pointer", width: "16px", height: "16px", marginRight: "8px" }}
                  />
                  <span style={{ fontSize: "14px", userSelect: "none" }}>Full Name (આખું નામ)</span>
                </div>
                <div 
                  className="d-flex align-items-center" 
                  style={{ cursor: "pointer" }} 
                  onClick={() => setNameConfig('split')}
                >
                  <input
                    type="radio"
                    name="nameConfig"
                    checked={nameConfig === 'split'}
                    onChange={() => setNameConfig('split')}
                    style={{ cursor: "pointer", width: "16px", height: "16px", marginRight: "8px" }}
                  />
                  <span style={{ fontSize: "14px", userSelect: "none" }}>First Name & Last Name (પ્રથમ નામ અને અટક)</span>
                </div>
              </div>
            </FormGroup>

            {(!isEdit || 
              (eventCreatedBy !== null && Number(eventCreatedBy) === Number(localStorage.getItem("userId"))) || 
              eventCreatedBy === null) && (
              <>
                <hr />
                <FormGroup className="mb-3">
                  <Label className="font-weight-bold">Shared User Access (સહભાગી વપરાશકર્તા ઍક્સેસ)</Label>
                  <div 
                    className="border p-3 rounded" 
                    style={{ 
                      maxHeight: "160px", 
                      overflowY: "auto", 
                      backgroundColor: "#f9fafb",
                      borderColor: "#e5e7eb",
                      boxShadow: "inset 0 1px 2px rgba(0,0,0,0.05)"
                    }}
                  >
                    {accessUsersList.length === 0 ? (
                      <span className="text-muted" style={{ fontSize: "13px" }}>No other temple users available.</span>
                    ) : (
                      accessUsersList.map((usr) => (
                        <div 
                          key={usr.userId} 
                          className="d-flex align-items-center mb-2" 
                          style={{ cursor: "pointer" }}
                          onClick={(e) => {
                            if (e.target.tagName === 'INPUT') return;
                            const isChecked = accessUserIds.includes(usr.userId);
                            if (!isChecked) {
                              setAccessUserIds([...accessUserIds, usr.userId]);
                            } else {
                              setAccessUserIds(accessUserIds.filter(id => id !== usr.userId));
                            }
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={accessUserIds.includes(usr.userId)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setAccessUserIds([...accessUserIds, usr.userId]);
                              } else {
                                setAccessUserIds(accessUserIds.filter(id => id !== usr.userId));
                              }
                            }}
                            style={{ cursor: "pointer", width: "16px", height: "16px", marginRight: "8px" }}
                          />
                          <span style={{ fontSize: "13px", userSelect: "none", color: "#374151" }}>
                            {usr.name} <span className="text-muted">({usr.email})</span>
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </FormGroup>
              </>
            )}

            <div className="mb-3 d-flex align-items-center" style={{ cursor: "pointer" }} onClick={() => setIsEditableByUser(!isEditableByUser)}>
              <input
                type="checkbox"
                checked={isEditableByUser}
                onChange={(e) => setIsEditableByUser(e.target.checked)}
                style={{ cursor: "pointer", width: "18px", height: "18px", marginRight: "10px" }}
              />
              <span style={{ fontSize: "14px", fontWeight: "bold", userSelect: "none", color: "#1f2937" }}>
                Allow Public Form Editing by Registrants (નોંધણી કરનાર માટે સાર્વજનિક ફોર્મ ફેરફાર કરવાની પરવાનગી આપો)
              </span>
            </div>

            <div className="mb-3 d-flex align-items-center" style={{ cursor: "pointer" }} onClick={() => setIsRegistrationActive(!isRegistrationActive)}>
              <input
                type="checkbox"
                checked={isRegistrationActive}
                onChange={(e) => setIsRegistrationActive(e.target.checked)}
                style={{ cursor: "pointer", width: "18px", height: "18px", marginRight: "10px" }}
              />
              <span style={{ fontSize: "14px", fontWeight: "bold", userSelect: "none", color: "#1f2937" }}>
                Registration Active (નોંધણી સક્રિય)
              </span>
            </div>

            <FormGroup className="mb-3">
              <Label className="font-weight-bold">Token Start Value (ટોકન પ્રારંભિક મૂલ્ય)</Label>
              <Input
                type="number"
                min="0"
                value={tokenStartValue}
                onChange={(e) => setTokenStartValue(e.target.value)}
                placeholder="e.g. 25 (Enter 0 or leave empty to disable token system)"
                style={{ borderRadius: "8px" }}
              />
              <small className="text-muted">
                Specify the starting value for sequential token generation (e.g. 25). If null, 0, or empty, the token system is disabled.
              </small>
            </FormGroup>

            {/* Payment Gateway Configuration */}
            <hr />
            <div className="mb-3">
              <h5 className="font-weight-bold mb-3" style={{ color: "#4a1b8a" }}>
                Payment Configuration (પેમેન્ટ સુયોજન)
              </h5>
              <div className="mb-3 d-flex align-items-center" style={{ cursor: "pointer" }} onClick={() => setIsPaymentEnabled(!isPaymentEnabled)}>
                <input
                  type="checkbox"
                  checked={isPaymentEnabled}
                  onChange={(e) => setIsPaymentEnabled(e.target.checked)}
                  style={{ cursor: "pointer", width: "18px", height: "18px", marginRight: "10px" }}
                />
                <span style={{ fontSize: "14px", fontWeight: "bold", userSelect: "none", color: "#1f2937" }}>
                  Enable Razorpay Payment (રેઝરપે પેમેન્ટ સક્રિય કરો)
                </span>
              </div>
              {isPaymentEnabled && (
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label className="font-weight-bold">Registration Fee (INR) <span className="text-danger">*</span></Label>
                      <Input
                        type="number"
                        min="1"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="e.g. 500"
                        required={isPaymentEnabled}
                        style={{ borderRadius: "8px" }}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label className="font-weight-bold">Select Trust <span className="text-danger">*</span></Label>
                      <Input
                        type="select"
                        value={trustId}
                        onChange={(e) => setTrustId(e.target.value)}
                        required={isPaymentEnabled}
                        style={{ borderRadius: "8px" }}
                      >
                        <option value="">-- Choose Trust --</option>
                        {trustList.map((trust) => (
                          <option key={trust.trustId} value={trust.trustId}>
                            {trust.trustName}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                </Row>
              )}
            </div>

            <hr />
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="m-0 font-weight-bold" style={{ color: "#4a1b8a" }}>
                Custom Registration Fields
              </h5>
              <Button
                color="info"
                size="sm"
                type="button"
                className="d-flex align-items-center gap-1 font-weight-bold text-white"
                onClick={handleAddField}
                style={{ borderRadius: "20px", backgroundColor: "#02a4b4", border: "none" }}
              >
                + Add Field
              </Button>
            </div>

            {formFields.length === 0 ? (
              <div className="text-center py-3 text-muted border rounded" style={{ borderStyle: "dashed", backgroundColor: "#f8f9fa" }}>
                No custom fields defined. Default registration fields (Name, Mobile, Gender, Address) will be collected.
              </div>
            ) : (
              <div style={{ maxHeight: "380px", overflowY: "auto", paddingRight: "5px" }}>
                {formFields.map((field, idx) => (
                  <Card
                    key={field.fieldKey || idx}
                    className="mb-3 border shadow-sm"
                    style={{ borderRadius: "8px", opacity: 1 }}
                    draggable
                    onDragStart={() => handleDragStart(idx)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, idx)}
                  >
                    <CardBody className="p-3">
                      <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                        <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                          <span
                            title="Drag to reorder"
                            style={{
                              cursor: 'grab',
                              fontSize: '18px',
                              color: '#adb5bd',
                              lineHeight: 1,
                              userSelect: 'none'
                            }}
                          >
                            ⠿
                          </span>
                          <span className="font-weight-bold text-muted">Field #{idx + 1}</span>
                        </div>
                        <Button
                          color="danger"
                          outline
                          size="xs"
                          type="button"
                          onClick={() => handleRemoveField(idx)}
                          style={{ padding: "2px 8px", fontSize: "12px", borderRadius: "4px" }}
                        >
                          Remove
                        </Button>
                      </div>
                      <Row>
                        <Col md="4" className="mb-2">
                          <Label size="sm" className="font-weight-bold">Field Key (Unique ID)</Label>
                          <Input
                            type="text"
                            size="sm"
                            value={field.fieldKey}
                            placeholder="e.g. home_mobile"
                            onChange={(e) => handleUpdateField(idx, 'fieldKey', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                            required
                          />
                        </Col>
                        <Col md="4" className="mb-2">
                          <Label size="sm" className="font-weight-bold">Field Label</Label>
                          <Input
                            type="text"
                            size="sm"
                            value={field.label}
                            placeholder="e.g. Home Mobile"
                            onChange={(e) => handleUpdateField(idx, 'label', e.target.value)}
                            required
                          />
                        </Col>
                        <Col md="4" className="mb-2">
                          <Label size="sm" className="font-weight-bold">Type</Label>
                          <Input
                            type="select"
                            size="sm"
                            value={field.type}
                            onChange={(e) => handleUpdateField(idx, 'type', e.target.value)}
                          >
                            <option value="text">Text Input</option>
                            <option value="number">Number Input</option>
                            <option value="textarea">Long Text (Textarea)</option>
                            <option value="dropdown">Dropdown Options</option>
                            <option value="radio">Radio Buttons</option>
                            <option value="checkbox">Checkboxes</option>
                            <option value="date">Date Picker</option>
                            <option value="email">Email</option>
                            <option value="phone">Phone</option>
                            <option value="mapLocation">Location from Map (Lat/Lng)</option>
                            <option value="file">File Upload</option>
                            <option value="label">Label / Info Text</option>
                          </Input>
                        </Col>
                      </Row>
                      <Row className="align-items-center mt-2">
                        {field.type !== 'label' && (
                          <Col md="4" className="mb-2">
                            <div className="d-flex align-items-center" style={{ cursor: "pointer" }} onClick={() => handleUpdateField(idx, 'required', !field.required)}>
                              <input
                                type="checkbox"
                                checked={!!field.required}
                                onChange={(e) => handleUpdateField(idx, 'required', e.target.checked)}
                                style={{ cursor: "pointer", width: "16px", height: "16px", marginRight: "8px" }}
                              />
                              <span style={{ fontSize: "13px", fontWeight: "bold", userSelect: "none", color: "#374151" }}>
                                Compulsory / Required
                              </span>
                            </div>
                          </Col>
                        )}
                        {field.type !== 'label' && (
                          <Col md="4" className="mb-2">
                            <div className="d-flex align-items-center" style={{ cursor: "pointer" }} onClick={() => handleUpdateField(idx, 'isEditable', !field.isEditable)}>
                              <input
                                type="checkbox"
                                checked={!!field.isEditable}
                                onChange={(e) => handleUpdateField(idx, 'isEditable', e.target.checked)}
                                style={{ cursor: "pointer", width: "16px", height: "16px", marginRight: "8px" }}
                              />
                              <span style={{ fontSize: "13px", fontWeight: "bold", userSelect: "none", color: "#374151" }}>
                                Editable by Registrant
                              </span>
                            </div>
                          </Col>
                        )}
                        {field.type !== 'label' && (
                          <Col md="4" className="mb-2">
                            <div className="d-flex align-items-center" style={{ cursor: "pointer" }} onClick={() => handleUpdateField(idx, 'showInList', !field.showInList)}>
                              <input
                                type="checkbox"
                                checked={!!field.showInList}
                                onChange={(e) => handleUpdateField(idx, 'showInList', e.target.checked)}
                                style={{ cursor: "pointer", width: "16px", height: "16px", marginRight: "8px" }}
                              />
                              <span style={{ fontSize: "13px", fontWeight: "bold", userSelect: "none", color: "#374151" }}>
                                Show on List Column
                              </span>
                            </div>
                          </Col>
                        )}
                        {field.type === 'number' && (
                          <>
                            <Col md="4" className="mb-2">
                              <div className="d-flex align-items-center" style={{ cursor: "pointer" }} onClick={() => handleUpdateField(idx, 'showSumCard', !field.showSumCard)}>
                                <input
                                  type="checkbox"
                                  checked={!!field.showSumCard}
                                  onChange={(e) => handleUpdateField(idx, 'showSumCard', e.target.checked)}
                                  style={{ cursor: "pointer", width: "16px", height: "16px", marginRight: "8px" }}
                                />
                                <span style={{ fontSize: "13px", fontWeight: "bold", userSelect: "none", color: "#374151" }}>
                                  Show Total Sum Card
                                </span>
                              </div>
                            </Col>
                            <Col md="4" className="mb-2">
                              <div className="d-flex align-items-center" style={{ cursor: "pointer" }} onClick={() => handleUpdateField(idx, 'isToken', !field.isToken)}>
                                <input
                                  type="checkbox"
                                  checked={!!field.isToken}
                                  onChange={(e) => handleUpdateField(idx, 'isToken', e.target.checked)}
                                  style={{ cursor: "pointer", width: "16px", height: "16px", marginRight: "8px" }}
                                />
                                <span style={{ fontSize: "13px", fontWeight: "bold", userSelect: "none", color: "#374151" }}>
                                  Is Token Field
                                </span>
                              </div>
                            </Col>
                          </>
                        )}
                        {['dropdown', 'radio', 'checkbox'].includes(field.type) && (
                          <Col md="8" className="mb-2">
                            <Label size="sm" className="font-weight-bold">Options (separated by comma)</Label>
                            <Input
                              type="text"
                              size="sm"
                              value={Array.isArray(field.options) ? field.options.join(', ') : (field.options || '')}
                              placeholder="e.g. Option 1, Option 2, Option 3"
                              onChange={(e) => handleUpdateField(idx, 'options', e.target.value)}
                              required
                            />
                          </Col>
                        )}
                      </Row>
                    </CardBody>
                  </Card>
                ))}
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setModalOpen(false)} style={{ borderRadius: "8px" }}>
              Cancel
            </Button>
            <Button
              color="primary"
              type="submit"
              disabled={submitting}
              style={{ borderRadius: "8px", backgroundColor: "#7c3abf", border: "none" }}
            >
              {isEdit ? (submitting ? "Updating..." : "Update Event") : (submitting ? "Creating..." : "Create Event")}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </Fragment>
  );
};

export default Events;
